import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Paperclip, Download, Trash2, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useFileNamePrompt, renamedFile } from "@/components/FileNamePrompt";

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);

export function EventAttachments({ event, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [askName, nameDialog] = useFileNamePrompt();
  const list = event.attachments || [];

  const rename = async (a) => {
    const name = await askName(a.original_filename, "Rinomina file");
    if (!name || name === a.original_filename) return;
    const { data } = await api.patch(`/calendar/${event.id}/attachments/${a.id}`, { name });
    onChange(list.map((x) => (x.id === a.id ? { ...x, original_filename: data.original_filename } : x)));
    toast.success("File rinominato");
  };

  const upload = async (files) => {
    if (!files?.length) return;
    if (ref.current) ref.current.value = "";
    const named = [];
    for (const f of files) {
      const n = await askName(f.name);
      if (n) named.push(renamedFile(f, n));
    }
    files = named;
    if (!files.length) return;
    setBusy(true);
    const added = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try { const { data } = await api.post(`/calendar/${event.id}/attachments`, fd); added.push(data); }
      catch (e) { toast.error(`${f.name}: ${formatApiErrorDetail(e.response?.data?.detail)}`); }
    }
    setBusy(false);
    if (ref.current) ref.current.value = "";
    if (added.length) { onChange([...list, ...added]); toast.success(added.length === 1 ? "File allegato" : `${added.length} file allegati`); }
  };

  const open = async (a) => {
    const { data } = await api.get(`/calendar/${event.id}/attachments/${a.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = url; link.download = a.original_filename; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const remove = async (a) => {
    await api.delete(`/calendar/${event.id}/attachments/${a.id}`);
    onChange(list.filter((x) => x.id !== a.id));
    toast.success("Allegato eliminato");
  };

  return (
    <div className="border-t border-border pt-2 mt-2" data-testid="event-attachments">
      {nameDialog}
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Paperclip className="h-3 w-3" /> Allegati ({list.length})</p>
        <input ref={ref} type="file" multiple className="hidden" data-testid="event-attachment-input" onChange={(e) => upload([...e.target.files])} />
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => ref.current?.click()} data-testid="event-attachment-add">
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Paperclip className="h-3.5 w-3.5 mr-1" />}Allega file
        </Button>
      </div>
      {list.map((a) => (
        <div key={a.id} className="flex items-center gap-2 py-1" data-testid={`event-attachment-${a.id}`}>
          <button type="button" onClick={() => open(a)} className="flex-1 min-w-0 truncate text-left text-xs font-medium text-primary hover:underline" data-testid={`event-attachment-open-${a.id}`}>{a.original_filename}</button>
          <span className="text-[10px] text-muted-foreground shrink-0">{fmtSize(a.size)}</span>
          <button type="button" onClick={() => open(a)} className="text-muted-foreground hover:text-foreground" aria-label="Scarica"><Download className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => rename(a)} className="text-muted-foreground hover:text-foreground" aria-label="Rinomina" data-testid={`event-attachment-rename-${a.id}`}><Pencil className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => remove(a)} className="text-muted-foreground hover:text-destructive" aria-label="Elimina" data-testid={`event-attachment-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
    </div>
  );
}
